<template>
  <div class="w-full p-4">
    <!-- The band stays on screen, as on My Expenses: title, the one line of
         context and the filters travel together. -mt-4 cancels the page's own
         p-4, which otherwise leaves 16px of slack before it catches. -->
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-2">
        <h2 class="text-lg font-semibold">Operations</h2>
      </div>

      <p class="text-sm text-gray-500">
        Recurring work by department — is today's work done?
      </p>

      <div class="mt-3 flex items-center gap-3">
        <label class="text-sm text-gray-600 flex items-center">
          As of
          <input
            type="date"
            v-model="asOf"
            class="ml-1 h-9 px-2 border border-gray-400 rounded-md text-sm"
            @change="load"
          />
        </label>
      </div>

      <!-- Departments as tabs rather than a dropdown. There are five of
           them and they are the first division anybody makes of this page
           -- "is HR's work done" is the question, not "filter by HR" -- so
           they are worth the row they take, and a dropdown hid which one
           you were on behind a closed box.

           Drawn as folder tabs, the same treatment as the work item form:
           the selected one is white, carries the accent along its top edge
           and covers the strip's line with its own bottom border.

           Nothing reloads on a click. Every department's tasks are in hand
           already -- fifteen rows in total -- so switching tabs is a change
           of what is drawn, not a request. -->
      <div class="mt-3 -mx-4 px-4 flex items-end gap-1 overflow-x-auto
                  border-b border-gray-300">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="px-4 py-1.5 text-sm rounded-t border border-t-2 -mb-px
                 whitespace-nowrap flex items-center gap-1.5"
          :class="tabClass(tab.key)"
          :title="tab.title"
          @click="departmentId = tab.key"
        >
          <span>{{ tab.label }}</span>
          <span
            class="text-xs tabular-nums"
            :class="tab.count ? 'text-gray-500' : 'text-gray-300'"
          >{{ tab.count }}</span>
          <!-- A department with something overdue in it says so on the tab,
               because the whole point of the tabs is that you are only
               looking at one of them at a time. -->
          <span
            v-if="tab.missed"
            class="h-1.5 w-1.5 rounded-full bg-red-600"
            aria-label="has misses"
          ></span>
        </button>
      </div>
    </div>

    <p v-if="loading" class="mt-6 text-gray-500">Loading…</p>
    <p v-else-if="error" class="mt-6 text-red-600">{{ error }}</p>

    <template v-else>
      <div class="mt-4 flex gap-4 text-sm">
        <span class="px-3 py-1 rounded bg-gray-100">
          {{ summary.due }} due
        </span>
        <span class="px-3 py-1 rounded bg-green-100 text-green-800">
          {{ summary.done }} done
        </span>
        <span v-if="summary.missed" class="px-3 py-1 rounded bg-red-100 text-red-800">
          {{ summary.missed }} with misses
        </span>
      </div>

      <p v-if="!visibleTasks.length" class="mt-6 text-gray-500">
        No active recurring tasks{{ departmentId ? ' for this department' : '' }} yet.
      </p>

      <div v-for="group in grouped" :key="group.name" class="mt-6">
        <!-- Only while every department is on screen at once. With a tab
             chosen, the tab above says whose work this is and a heading
             repeating it is a second label for one list. -->
        <h3
          v-if="!departmentId"
          class="font-semibold text-dark-primary border-b border-gray-300 pb-1 mb-2
                 bg-gray-200 sticky z-20"
          :style="{ top: (topStickyHeight + NAVBAR_HEIGHT) + 'px' }"
        >
          {{ group.name }}
        </h3>

        <div
          v-for="row in group.rows"
          :key="row.id"
          class="border border-gray-300 rounded-md bg-white p-3 mb-2
                 flex items-start justify-between gap-4"
          :class="rowClass(row)"
        >
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium">{{ row.title }}</span>
              <span class="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                {{ row.cadence }}
              </span>
              <span v-if="row.sub_department_name" class="text-xs text-gray-500">
                {{ row.sub_department_name }}
              </span>
              <a
                v-if="row.reference_url"
                :href="row.reference_url"
                class="text-xs text-accent-primary underline"
              >open</a>
            </div>

            <p v-if="row.instructions" class="text-sm text-gray-600 mt-1">
              {{ row.instructions }}
            </p>

            <p class="text-xs text-gray-500 mt-1">
              <span v-if="row.owner_display">Owner: {{ row.owner_display }} · </span>
              <span v-if="row.current">Period {{ row.current.period }}</span>
              <span v-else>Not scheduled today</span>
              <span v-if="row.current && row.current.completed_by">
                · done by {{ row.current.completed_by }}
              </span>
            </p>

            <p v-if="row.misses.length" class="text-xs text-red-700 mt-1">
              Missed: {{ row.misses.map(m => m.period).join(', ') }}
            </p>
          </div>

          <div v-if="row.current" class="flex items-center gap-2 shrink-0">
            <button
              class="px-3 py-1.5 rounded-md text-sm border"
              :class="row.current.status === 'done'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white border-gray-400 hover:bg-gray-100'"
              :disabled="saving === row.id"
              @click="toggle(row, 'done')"
            >
              {{ row.current.status === 'done' ? 'Done' : 'Mark done' }}
            </button>
            <button
              class="px-3 py-1.5 rounded-md text-sm border"
              :class="row.current.status === 'skipped'
                ? 'bg-gray-500 text-white border-gray-500'
                : 'bg-white border-gray-400 hover:bg-gray-100'"
              :disabled="saving === row.id"
              @click="toggle(row, 'skipped')"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { useStickyOffset } from '@/composables/useStickyOffset'
import { getDashboard, getDepartments, markTaskPeriod } from '@/services/BackOffice'

// The app's navbar is fixed at this height, as on the backlog board.
const NAVBAR_HEIGHT = 56

// Measured, not assumed: the band holds a filter row that wraps at narrow
// widths, and a department heading offset by a stale number overlaps it.
const topStickyRef = ref(null)
const topStickyHeight = useStickyOffset(topStickyRef)

const store = useStore()

const tasks = ref([])
const departments = ref([])
const departmentId = ref('')
const asOf = ref(new Date().toISOString().slice(0, 10))
const loading = ref(true)
const saving = ref(null)
const error = ref('')

const token = () => store.getters['auth/showToken']

// What the chosen tab leaves on screen. '' is every department, which is
// also what the page opens on.
const visibleTasks = computed(() => (
  departmentId.value
    ? tasks.value.filter(row => row.department_id === departmentId.value)
    : tasks.value
))

// One per department, plus All in front. Built from the taxonomy rather
// than from the rows, so a department with no runbook yet still gets a tab
// saying so -- which is the useful reading of an empty one here.
const tabs = computed(() => {
  const counted = department => ({
    count: tasks.value.filter(row => row.department_id === department).length,
    missed: tasks.value.some(
      row => row.department_id === department && row.misses.length,
    ),
  })
  return [
    {
      key: '',
      label: 'All',
      title: 'Every department',
      count: tasks.value.length,
      missed: tasks.value.some(row => row.misses.length),
    },
    ...departments.value.map(department => ({
      key: department.id,
      label: department.name,
      title: `Recurring work owned by ${department.name}`,
      ...counted(department.id),
    })),
  ]
})

// Both states described once here rather than once per tab in the markup,
// the same helper the work item form uses.
function tabClass(key) {
  return departmentId.value === key
    ? 'bg-white text-dark-primary font-semibold border-gray-300 '
      + 'border-t-accent-primary border-b-white'
    : 'text-gray-500 border-transparent hover:bg-white/70 hover:text-dark-primary'
}

const summary = computed(() => ({
  due: visibleTasks.value.filter(t => t.current).length,
  done: visibleTasks.value.filter(t => t.current && t.current.status === 'done').length,
  missed: visibleTasks.value.filter(t => t.misses.length).length,
}))

const grouped = computed(() => {
  const groups = []
  for (const row of visibleTasks.value) {
    let group = groups.find(g => g.name === row.department_name)
    if (!group) {
      group = { name: row.department_name, rows: [] }
      groups.push(group)
    }
    group.rows.push(row)
  }
  return groups
})

function rowClass(row) {
  if (row.misses.length) return 'border-red-300 bg-red-50'
  if (row.current && row.current.status === 'done') return 'border-green-200 bg-green-50'
  if (row.current && row.current.status === 'skipped') return 'border-gray-300 bg-gray-50'
  return ''
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    // Every department, whichever tab is showing: the counts on the tabs
    // are counts of what is behind them, so they cannot be read off a
    // response that was filtered down to one.
    const data = await getDashboard(token(), { as_of: asOf.value })
    tasks.value = data.tasks
  } catch (e) {
    error.value = describe(e)
  } finally {
    loading.value = false
  }
}

async function toggle(row, status) {
  // Clicking the active state again reverts the period to pending, so a
  // mis-click is undoable without an extra control.
  const next = row.current.status === status ? 'pending' : status
  saving.value = row.id
  try {
    await markTaskPeriod(token(), {
      recurring_task: row.id,
      period: row.current.period,
      status: next,
    })
    await load()
  } catch (e) {
    error.value = describe(e)
  } finally {
    saving.value = null
  }
}

function describe(e) {
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

onMounted(async () => {
  try {
    departments.value = await getDepartments(token())
  } catch (e) {
    // Non-fatal: the filter just stays empty.
  }
  await load()
})
</script>
